import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { MARKET_API_BASE, reportUnauthorized } from "../backendConfig";
import NavBar from "./NavBar";
import AdminTraffic, { ago } from "./AdminTraffic";
import { fonts, pageStyles, palette } from "./pageStyles";

const CURRENCIES = ["USD", "SAR", "INR", "AED", "EUR", "GBP"];
const METHODS = ["Bank transfer", "UPI", "Cash", "Card", "PayPal", "Stripe", "Crypto", "Other"];
const STATUSES = [
  ["paid", "Paid"],
  ["pending", "Pending"],
  ["refunded", "Refunded"],
  ["void", "Void (entered by mistake)"],
];
const STATUS_COLOR = { paid: palette.up, pending: palette.highlight, refunded: palette.down, void: palette.mutedText };

const s = {
  body: { flex: 1, padding: "20px 16px 40px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 1280, width: "100%", margin: "0 auto", boxSizing: "border-box" },
  panel: { background: palette.glass, backdropFilter: "blur(8px)", border: `1px solid ${palette.border}`, borderRadius: 6, padding: 16, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
  h1: { margin: 0, fontFamily: fonts.display, fontSize: "1.4rem", fontWeight: 600 },
  h2: { margin: 0, fontFamily: fonts.display, fontSize: "1rem", fontWeight: 600 },
  small: { fontSize: "0.75rem", color: palette.mutedText },
  row: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  field: { padding: "8px 10px", borderRadius: 4, border: `1px solid ${palette.border}`, background: palette.field, color: palette.text, fontSize: "0.85rem", minHeight: 36, boxSizing: "border-box", fontFamily: "inherit" },
  btn: { padding: "8px 14px", minHeight: 36, borderRadius: 8, border: "none", background: palette.accent, color: palette.accentText, fontWeight: 650, fontSize: "0.82rem", cursor: "pointer", fontFamily: fonts.ui },
  btnGhost: { padding: "6px 10px", minHeight: 32, borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: "rgba(255,255,255,.025)", color: palette.text, fontSize: "0.78rem", fontWeight: 550, cursor: "pointer", fontFamily: fonts.ui },
  btnDanger: { padding: "6px 10px", minHeight: 32, borderRadius: 8, border: `1px solid ${palette.danger}`, background: palette.dangerBg, color: palette.danger, fontSize: "0.78rem", cursor: "pointer", fontWeight: 600, fontFamily: fonts.ui },
  lbl: { display: "flex", flexDirection: "column", gap: 4, fontSize: "0.72rem", color: palette.mutedText },
  th: { textAlign: "left", padding: "8px 8px", borderBottom: `1px solid ${palette.border}`, color: palette.mutedText, fontWeight: 500, fontSize: "0.72rem", whiteSpace: "nowrap" },
  td: { padding: "8px 8px", borderBottom: `1px solid ${palette.border}`, fontSize: "0.82rem", verticalAlign: "middle" },
};

function money(amount, currency) {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}
function moneyMinor(amountMinor, currency) {
  try {
    const digits = new Intl.NumberFormat(undefined, { style: "currency", currency }).resolvedOptions().maximumFractionDigits;
    return money(Number(amountMinor || 0) / (10 ** digits), currency);
  } catch { return money(Number(amountMinor || 0) / 100, currency); }
}
function moneyMap(map) {
  const entries = Object.entries(map || {});
  return entries.length ? entries.map(([cur, v]) => money(v, cur)).join(" + ") : "-";
}
const day = (iso) => (iso ? iso.slice(0, 10) : "-");
const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = (over = {}) => ({
  id: null,
  customer_email: "",
  customer_name: "",
  amount: "",
  currency: "USD",
  method: "Bank transfer",
  plan: "",
  status: "paid",
  paid_date: todayIso(),
  note: "",
  ...over,
});

export default function AdminPage() {
  const { token, user } = useAuth();
  const [access, setAccess] = useState("checking"); // checking | ok | denied | error
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("traffic");
  const [userQuery, setUserQuery] = useState("");
  const [payQuery, setPayQuery] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null); // user id waiting for "Yes, reset"
  const [temp, setTemp] = useState(null); // {email, password}
  const [copied, setCopied] = useState(false);
  const [brokerAccount, setBrokerAccount] = useState(null);
  const [brokerHistory, setBrokerHistory] = useState(null);
  const [brokerHistoryDays, setBrokerHistoryDays] = useState(30);
  const [brokerLoading, setBrokerLoading] = useState(false);
  const [brokerError, setBrokerError] = useState("");
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [marketplaceStatus, setMarketplaceStatus] = useState("pending_review");
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);

  const api = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${MARKET_API_BASE}/admin${path}`, {
        ...options,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
      });
      if (!res.ok) {
        reportUnauthorized(res.status, Boolean(token)); // expired login: sign out and go to /login
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        const err = new Error(
          typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg || d).join("; ") : `Request failed (${res.status})`
        );
        err.status = res.status;
        throw err;
      }
      return res;
    },
    [token]
  );

  const loadOverview = useCallback(async () => {
    const res = await api("/overview");
    setOverview(await res.json());
  }, [api]);
  const loadUsers = useCallback(
    async (q = "") => {
      const res = await api(`/users?q=${encodeURIComponent(q)}`);
      setUsers(await res.json());
    },
    [api]
  );
  const loadPayments = useCallback(
    async (q = "", status = "") => {
      const res = await api(`/payments?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}`);
      setPayments(await res.json());
    },
    [api]
  );
  const loadMarketplaceListings = useCallback(async () => {
    setMarketplaceLoading(true);
    try {
      const res = await api(`/marketplace/listings?status_filter=${encodeURIComponent(marketplaceStatus)}`);
      const data = await res.json();
      setMarketplaceListings(data.items || []);
    } finally {
      setMarketplaceLoading(false);
    }
  }, [api, marketplaceStatus]);

  // first load also tells us whether this account may open the page at all
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([loadOverview(), loadUsers(""), loadPayments("", "")]);
        if (!cancelled) setAccess("ok");
      } catch (err) {
        if (cancelled) return;
        if (err.status === 403) setAccess("denied");
        else {
          setError(err.message);
          setAccess("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOverview, loadUsers, loadPayments]);

  useEffect(() => {
    if (access !== "ok") return undefined;
    const t = setTimeout(() => loadUsers(userQuery).catch((e) => setError(e.message)), 250);
    return () => clearTimeout(t);
  }, [userQuery, access, loadUsers]);
  useEffect(() => {
    if (access !== "ok") return undefined;
    const t = setTimeout(() => loadPayments(payQuery, payStatus).catch((e) => setError(e.message)), 250);
    return () => clearTimeout(t);
  }, [payQuery, payStatus, access, loadPayments]);

  const refreshAll = useCallback(async () => {
    try {
      await Promise.all([loadOverview(), loadUsers(userQuery), loadPayments(payQuery, payStatus)]);
    } catch (err) {
      setError(err.message);
    }
  }, [loadOverview, loadUsers, loadPayments, userQuery, payQuery, payStatus]);

  const loadBroker = useCallback(async () => {
    setBrokerLoading(true);
    setBrokerError("");
    setBrokerAccount(null);
    setBrokerHistory(null);
    try {
      const [accountResponse, historyResponse] = await Promise.all([api("/broker/account"), api(`/broker/history?days=${brokerHistoryDays}`)]);
      setBrokerAccount(await accountResponse.json());
      setBrokerHistory(await historyResponse.json());
    } catch (err) {
      setBrokerError(err.message || "Could not load broker data.");
    } finally {
      setBrokerLoading(false);
    }
  }, [api, brokerHistoryDays]);

  useEffect(() => {
    if (access === "ok" && tab === "broker") loadBroker();
  }, [access, tab, loadBroker]);

  useEffect(() => {
    if (access === "ok" && tab === "marketplace") loadMarketplaceListings().catch((err) => setError(err.message));
  }, [access, tab, loadMarketplaceListings]);

  const setMarketplaceListingStatus = async (listingId, nextStatus) => {
    setError("");
    try {
      await api(`/marketplace/listings/${listingId}`, { method: "POST", body: JSON.stringify({ status: nextStatus }) });
      await loadMarketplaceListings();
    } catch (err) { setError(err.message); }
  };

  const plans = useMemo(() => [...new Set(["ALGO MARVICKS course", ...payments.map((p) => p.plan).filter(Boolean)])], [payments]);

  const resetPassword = async (u) => {
    setError("");
    setTemp(null);
    setCopied(false);
    try {
      const res = await api(`/users/${u.id}/reset-password`, { method: "POST" });
      const data = await res.json();
      setTemp({ email: data.email, password: data.temporary_password });
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmReset(null);
    }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(temp.password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const openNewPayment = (over) => {
    setFormError("");
    setForm(emptyForm(over));
    setTab("payments");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const openEdit = (p) => {
    setFormError("");
    setForm({
      id: p.id,
      customer_email: p.customer_email,
      customer_name: p.customer_name,
      amount: String(p.amount),
      currency: p.currency,
      method: p.method,
      plan: p.plan,
      status: p.status,
      paid_date: day(p.paid_at),
      note: p.note,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setFormError("");
    const amount = Number(form.amount);
    if (!(amount > 0)) {
      setFormError("Enter an amount above 0.");
      return;
    }
    const body = {
      customer_email: form.customer_email.trim(),
      customer_name: form.customer_name.trim(),
      amount,
      currency: form.currency.trim().toUpperCase(),
      method: form.method.trim(),
      plan: form.plan.trim(),
      status: form.status,
      paid_at: `${form.paid_date || todayIso()}T12:00:00`,
      note: form.note.trim(),
    };
    setSaving(true);
    try {
      await api(form.id ? `/payments/${form.id}` : "/payments", { method: "POST", body: JSON.stringify(body) });
      setForm(null);
      await refreshAll();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await api(`/payments/${p.id}`, { method: "POST", body: JSON.stringify({ status }) });
      await refreshAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api("/payments.csv");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payments.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  if (access === "checking") {
    return (
      <div style={pageStyles.page}>
        <NavBar />
        <div style={s.body}><div style={s.small}>Loading... (the free server can take about a minute to wake up)</div></div>
      </div>
    );
  }
  if (access === "denied") {
    return (
      <div style={pageStyles.page}>
        <NavBar />
        <div style={s.body}>
          <div style={s.panel}>
            <h1 style={s.h1}>Admin</h1>
            <div style={s.small}>This page is only for the site owner. You are signed in as {user?.email}.</div>
            <Link to="/app" style={pageStyles.link}>Back to chart</Link>
          </div>
        </div>
      </div>
    );
  }
  if (access === "error") {
    return (
      <div style={pageStyles.page}>
        <NavBar />
        <div style={s.body}>
          <div style={s.panel}>
            <h1 style={s.h1}>Admin</h1>
            <div style={pageStyles.errorBanner}>Could not load: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  const monthsByCurrency = {};
  (overview?.monthly || []).forEach((m) => {
    (monthsByCurrency[m.currency] = monthsByCurrency[m.currency] || []).push(m);
  });

  return (
    <div style={pageStyles.page}>
      <NavBar />
      <div style={s.body}>
        <div style={{ ...s.row, justifyContent: "space-between" }}>
          <h1 style={s.h1}>Admin</h1>
          <span style={s.small}>Signed in as {user?.email}</span>
        </div>
        {error && (
          <div style={pageStyles.errorBanner} role="alert">
            {error} <button type="button" style={{ ...pageStyles.link, marginLeft: 8 }} onClick={() => setError("")}>dismiss</button>
          </div>
        )}

        {/* ---------------- numbers ---------------- */}
        <div style={s.row}>
          <Card label="Users" value={String(overview?.users_total ?? 0)} sub={`${overview?.new_users_30d ?? 0} new in 30 days`} />
          {(overview?.currencies || []).map((c) => (
            <Card key={c.currency} label={`Paid (${c.currency})`} value={money(c.paid_total, c.currency)} sub={`${money(c.paid_this_month, c.currency)} this month · ${c.paid_count} payments${c.pending_total ? ` · ${money(c.pending_total, c.currency)} pending` : ""}${c.refunded_total ? ` · ${money(c.refunded_total, c.currency)} refunded` : ""}`} />
          ))}
          {!(overview?.currencies || []).length && <Card label="Payments" value="None yet" sub="Add your first one on the Payments tab" />}
        </div>

        {temp && (
          <div style={{ ...s.panel, borderColor: palette.highlight }} role="status">
            <h2 style={s.h2}>Temporary password for {temp.email}</h2>
            <div style={s.row}>
              <code style={{ ...s.field, fontFamily: fonts.code, fontSize: "1.05rem", letterSpacing: "0.06em", userSelect: "all" }}>{temp.password}</code>
              <button type="button" style={s.btn} onClick={copyTemp}>{copied ? "Copied" : "Copy"}</button>
              <button type="button" style={s.btnGhost} onClick={() => setTemp(null)}>Hide</button>
            </div>
            <div style={s.small}>
              Shown only now: it is stored scrambled and nobody can read it again. Send it to the user privately. They were signed out everywhere and can sign in with it, then change it
              on their Account page. Passwords of other users can never be viewed, only replaced.
            </div>
          </div>
        )}

        <div role="tablist" aria-label="Admin sections" style={{ display: "flex", gap: 5, padding: 4, alignSelf: "flex-start", border: `1px solid ${palette.border}`, borderRadius: 10, background: "rgba(255,255,255,.025)", maxWidth: "100%", overflowX: "auto" }}>
          {[["traffic", "Traffic"], ["users", `Users (${users.length})`], ["payments", `Payments (${payments.length})`], ["marketplace", "Marketplace review"], ["broker", "Broker account"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              style={{ ...pageStyles.navLink, minHeight: 34, padding: "0 13px", borderRadius: 7, color: tab === id ? palette.text : palette.mutedText, background: tab === id ? palette.selected : "transparent", fontWeight: tab === id ? 650 : 550, letterSpacing: "0.01em", boxShadow: "none" }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ---------------- traffic ---------------- */}
        {tab === "traffic" && <AdminTraffic api={api} onError={setError} />}

        {/* ---------------- marketplace moderation ---------------- */}
        {tab === "marketplace" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ ...s.panel, flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <div><h2 style={s.h2}>Marketplace submissions</h2><div style={s.small}>Review creator listings and package content before they become visible to buyers.</div></div>
              <div style={s.row}>
                <select aria-label="Listing status" style={s.field} value={marketplaceStatus} onChange={(event) => setMarketplaceStatus(event.target.value)}>
                  {[["pending_review", "Awaiting review"], ["published", "Published"], ["rejected", "Rejected"], ["paused", "Paused"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <button type="button" style={s.btnGhost} onClick={() => loadMarketplaceListings().catch((err) => setError(err.message))} disabled={marketplaceLoading}>{marketplaceLoading ? "Loading…" : "Refresh"}</button>
              </div>
            </div>
            {marketplaceListings.map((listing) => (
              <article key={listing.id} style={s.panel}>
                <div style={{ ...s.row, justifyContent: "space-between" }}>
                  <div><h3 style={{ ...s.h2, fontSize: "0.95rem" }}>{listing.title}</h3><div style={s.small}>{listing.category.replaceAll("_", " ")} · {listing.seller?.display_name || "Unknown creator"} · {listing.price_minor === 0 ? "Free" : moneyMinor(listing.price_minor, listing.currency)} · package {Math.ceil(listing.artifact_size / 1024)} KB</div></div>
                  <span style={{ color: listing.status === "published" ? palette.up : palette.highlight, fontSize: 11 }}>{listing.status.replaceAll("_", " ")}</span>
                </div>
                <p style={{ margin: 0, color: palette.mutedText, fontSize: 12, lineHeight: 1.5 }}>{listing.description}</p>
                <details>
                  <summary style={{ color: palette.accentBlue, fontSize: 12, cursor: "pointer" }}>Review submitted package content</summary>
                  <pre style={{ maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere", padding: 12, borderRadius: 8, background: palette.field, color: palette.text, fontSize: 11, fontFamily: fonts.code }}>{JSON.stringify(listing.review_artifact, null, 2)}</pre>
                </details>
                <div style={s.row}>
                  {listing.status !== "published" && <button type="button" style={s.btn} onClick={() => setMarketplaceListingStatus(listing.id, "published")}>Approve and publish</button>}
                  {listing.status !== "rejected" && <button type="button" style={s.btnDanger} onClick={() => setMarketplaceListingStatus(listing.id, "rejected")}>Reject</button>}
                  {listing.status === "published" && <button type="button" style={s.btnGhost} onClick={() => setMarketplaceListingStatus(listing.id, "paused")}>Pause listing</button>}
                </div>
              </article>
            ))}
            {!marketplaceListings.length && <div style={s.panel}><span style={s.small}>{marketplaceLoading ? "Loading marketplace submissions…" : "No listings in this status."}</span></div>}
          </div>
        )}

        {/* ---------------- broker (owner-only, read-only) ---------------- */}
        {tab === "broker" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <div>
                  <h2 style={s.h2}>cTrader account connection</h2>
                  <div style={s.small}>Read-only view of the single account configured on the backend.</div>
                </div>
                <button type="button" style={s.btnGhost} onClick={loadBroker} disabled={brokerLoading}>{brokerLoading ? "Loading…" : "Refresh"}</button>
              </div>
              {brokerError && <div style={pageStyles.errorBanner} role="alert">{brokerError}</div>}
              {!brokerError && !brokerAccount && <div style={s.small}>{brokerLoading ? "Loading account details…" : "Open Broker account to load the current account."}</div>}
              {brokerAccount && (
                <>
                  <div style={{ ...s.row, gap: 10 }}>
                    {[
                      ["Connection", "Connected"],
                      ["Account mode", String(brokerAccount.mode || "unknown").toUpperCase()],
                      ["Broker", brokerAccount.account?.broker || "cTrader"],
                      ["Account type", brokerAccount.account?.account_type || "—"],
                      ["Balance", `${Number(brokerAccount.account?.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${brokerAccount.account?.deposit_asset || ""}`],
                      ["Open-position margin", Number(brokerAccount.account?.positions_used_margin || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })],
                      ["Leverage", brokerAccount.account?.leverage ? `${brokerAccount.account.leverage}:1` : "—"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ ...s.panel, flex: "1 1 135px", padding: 12, gap: 5 }}>
                        <span style={s.small}>{label}</span><strong style={{ fontFamily: fonts.display, fontSize: "0.95rem" }}>{value}</strong>
                      </div>
                    ))}
                  </div>
                  <div style={{ ...s.small, color: brokerAccount.mode === "live" ? palette.highlight : palette.mutedText }}>
                    {brokerAccount.mode === "live" ? "Live account data. This page cannot place or change orders." : "Demo account data. Order placement and changes are disabled."}
                    {brokerAccount.note ? ` ${brokerAccount.note}` : ""}
                  </div>
                </>
              )}
            </div>

            {brokerAccount && (
              <div style={s.panel}>
                <h2 style={s.h2}>Open positions</h2>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
                    <thead><tr>{["Position", "Instrument", "Side", "Volume", "Entry", "Stop loss", "Take profit", "Used margin"].map((label) => <th key={label} style={s.th}>{label}</th>)}</tr></thead>
                    <tbody>
                      {(brokerAccount.positions || []).map((position) => (
                        <tr key={position.position_id}>
                          {[position.position_id, position.symbol, position.side, position.volume, position.entry_price ?? "—", position.stop_loss ?? "—", position.take_profit ?? "—", position.used_margin].map((value, index) => <td key={`${position.position_id}-${index}`} style={{ ...s.td, color: index === 2 ? (position.side === "BUY" ? palette.up : palette.down) : palette.text }}>{value}</td>)}
                        </tr>
                      ))}
                      {!(brokerAccount.positions || []).length && <tr><td style={s.td} colSpan={8}><span style={s.small}>No open positions.</span></td></tr>}
                    </tbody>
                  </table>
                </div>
                <h2 style={s.h2}>Pending orders</h2>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
                    <thead><tr>{["Order", "Instrument", "Side", "Type", "Status", "Volume", "Limit", "Stop"].map((label) => <th key={label} style={s.th}>{label}</th>)}</tr></thead>
                    <tbody>
                      {(brokerAccount.pending_orders || []).map((order) => (
                        <tr key={order.order_id}>
                          {[order.order_id, order.symbol, order.side, order.type, order.status, order.volume, order.limit_price ?? "—", order.stop_price ?? "—"].map((value, index) => <td key={`${order.order_id}-${index}`} style={s.td}>{value}</td>)}
                        </tr>
                      ))}
                      {!(brokerAccount.pending_orders || []).length && <tr><td style={s.td} colSpan={8}><span style={s.small}>No pending orders.</span></td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {brokerAccount && (
              <div style={s.panel}>
                <div style={{ ...s.row, justifyContent: "space-between" }}>
                  <h2 style={s.h2}>Order history</h2>
                  <label style={{ ...s.lbl, flexDirection: "row", alignItems: "center", gap: 8 }}>
                    Range
                    <select style={s.field} value={brokerHistoryDays} onChange={(event) => setBrokerHistoryDays(Number(event.target.value))}>
                      {[7, 30, 90].map((days) => <option key={days} value={days}>Last {days} days</option>)}
                    </select>
                  </label>
                </div>
                {brokerHistory?.truncated && <div style={s.small}>The broker returned a partial result for this period. Choose a shorter period to see more orders.</div>}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
                    <thead><tr>{["Updated", "Order", "Instrument", "Side", "Type", "Status", "Volume", "Price"].map((label) => <th key={label} style={s.th}>{label}</th>)}</tr></thead>
                    <tbody>
                      {(brokerHistory?.orders || []).map((order) => (
                        <tr key={order.order_id}>
                          {[order.updated_at_ms ? new Date(order.updated_at_ms).toLocaleString() : "—", order.order_id, order.symbol, order.side, order.type, order.status, order.volume, order.limit_price ?? order.stop_price ?? "—"].map((value, index) => <td key={`${order.order_id}-${index}`} style={s.td}>{value}</td>)}
                        </tr>
                      ))}
                      {!brokerLoading && !(brokerHistory?.orders || []).length && <tr><td style={s.td} colSpan={8}><span style={s.small}>No orders returned for this period.</span></td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- users ---------------- */}
        {tab === "users" && (
          <div style={s.panel}>
            <div style={s.row}>
              <input style={{ ...s.field, flex: "1 1 220px" }} placeholder="Search name or email" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} aria-label="Search users" />
              <span style={s.small}>Passwords are stored scrambled, so they cannot be shown. You can set a new temporary one.</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 940 }}>
                <thead>
                  <tr>
                    {["Name", "Email", "Joined", "Last seen", "Page views (30 d)", "Payments", "Paid", "Last payment", ""].map((h) => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td style={s.td}>{u.display_name || "-"}{u.is_admin ? <span style={{ ...s.small, marginLeft: 6, color: palette.highlight }}>admin</span> : null}</td>
                      <td style={s.td}>{u.email}</td>
                      <td style={s.td}>{day(u.created_at)}</td>
                      <td style={s.td} title={u.last_seen_at || ""}>{ago(u.last_seen_at)}</td>
                      <td style={s.td}>{u.views_30d ?? 0}</td>
                      <td style={s.td}>{u.payments_count}</td>
                      <td style={s.td}>{moneyMap(u.paid)}</td>
                      <td style={s.td}>{day(u.last_payment_at)}</td>
                      <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                        <div style={s.row}>
                          <button type="button" style={s.btnGhost} onClick={() => openNewPayment({ customer_email: u.email, customer_name: u.display_name })}>Add payment</button>
                          {confirmReset === u.id ? (
                            <>
                              <button type="button" style={s.btnDanger} onClick={() => resetPassword(u)}>Yes, reset</button>
                              <button type="button" style={s.btnGhost} onClick={() => setConfirmReset(null)}>Cancel</button>
                            </>
                          ) : (
                            <button type="button" style={s.btnGhost} onClick={() => { setTemp(null); setConfirmReset(u.id); }}>Reset password</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!users.length && (
                    <tr><td style={s.td} colSpan={9}><span style={s.small}>No users found.</span></td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {confirmReset !== null && (
              <div style={s.small}>Resetting signs that user out everywhere and replaces their password with a new random one.</div>
            )}
          </div>
        )}

        {/* ---------------- payments ---------------- */}
        {tab === "payments" && (
          <>
            {form && (
              <form onSubmit={savePayment} style={s.panel} aria-label={form.id ? "Edit payment" : "Add payment"}>
                <h2 style={s.h2}>{form.id ? `Edit payment #${form.id}` : "Add a payment"}</h2>
                <div style={s.small}>This is a record you type in by hand. It does not charge anyone.</div>
                <div style={s.row}>
                  <label style={s.lbl}>
                    Customer email
                    <input style={{ ...s.field, width: 230 }} list="admin-emails" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="matches a user automatically" />
                  </label>
                  <label style={s.lbl}>
                    Name
                    <input style={{ ...s.field, width: 170 }} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                  </label>
                  <label style={s.lbl}>
                    Amount
                    <input style={{ ...s.field, width: 110 }} type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required aria-label="Amount" />
                  </label>
                  <label style={s.lbl}>
                    Currency
                    <input style={{ ...s.field, width: 80 }} list="admin-currencies" maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} aria-label="Currency" />
                  </label>
                </div>
                <div style={s.row}>
                  <label style={s.lbl}>
                    Plan / product
                    <input style={{ ...s.field, width: 230 }} list="admin-plans" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} />
                  </label>
                  <label style={s.lbl}>
                    Method
                    <input style={{ ...s.field, width: 150 }} list="admin-methods" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} />
                  </label>
                  <label style={s.lbl}>
                    Status
                    <select style={s.field} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} aria-label="Status">
                      {STATUSES.map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label style={s.lbl}>
                    Date
                    <input style={s.field} type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} aria-label="Payment date" />
                  </label>
                  <label style={{ ...s.lbl, flex: "1 1 220px" }}>
                    Note
                    <input style={s.field} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} />
                  </label>
                </div>
                <datalist id="admin-emails">{users.map((u) => <option key={u.id} value={u.email} />)}</datalist>
                <datalist id="admin-currencies">{CURRENCIES.map((c) => <option key={c} value={c} />)}</datalist>
                <datalist id="admin-methods">{METHODS.map((m) => <option key={m} value={m} />)}</datalist>
                <datalist id="admin-plans">{plans.map((p) => <option key={p} value={p} />)}</datalist>
                {formError && <div style={pageStyles.errorBanner} role="alert">{formError}</div>}
                <div style={s.row}>
                  <button type="submit" style={s.btn} disabled={saving}>{saving ? "Saving..." : form.id ? "Save changes" : "Save payment"}</button>
                  <button type="button" style={s.btnGhost} onClick={() => setForm(null)}>Cancel</button>
                </div>
              </form>
            )}

            <div style={s.panel}>
              <div style={s.row}>
                <input style={{ ...s.field, flex: "1 1 200px" }} placeholder="Search customer, plan or note" value={payQuery} onChange={(e) => setPayQuery(e.target.value)} aria-label="Search payments" />
                <select style={s.field} value={payStatus} onChange={(e) => setPayStatus(e.target.value)} aria-label="Filter by status">
                  <option value="">All statuses</option>
                  {STATUSES.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <button type="button" style={s.btn} onClick={() => openNewPayment()}>Add payment</button>
                <button type="button" style={s.btnGhost} onClick={downloadCsv}>Download CSV</button>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 820 }}>
                  <thead>
                    <tr>
                      {["Date", "Customer", "Plan", "Method", "Amount", "Status", "Note", ""].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td style={s.td}>{day(p.paid_at)}</td>
                        <td style={s.td}>{p.customer_name || "-"}<div style={s.small}>{p.customer_email}{p.user_id ? "" : p.customer_email ? " (no account)" : ""}</div></td>
                        <td style={s.td}>{p.plan || "-"}</td>
                        <td style={s.td}>{p.method || "-"}</td>
                        <td style={{ ...s.td, fontFamily: fonts.display, whiteSpace: "nowrap", textDecoration: p.status === "void" ? "line-through" : "none" }}>{money(p.amount, p.currency)}</td>
                        <td style={{ ...s.td, color: STATUS_COLOR[p.status] }}>{p.status}</td>
                        <td style={{ ...s.td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{p.note}</td>
                        <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                          <div style={s.row}>
                            <button type="button" style={s.btnGhost} onClick={() => openEdit(p)}>Edit</button>
                            {p.status === "paid" && <button type="button" style={s.btnGhost} onClick={() => setStatus(p, "refunded")}>Mark refunded</button>}
                            {p.status === "pending" && <button type="button" style={s.btnGhost} onClick={() => setStatus(p, "paid")}>Mark paid</button>}
                            {p.status !== "void" && <button type="button" style={s.btnGhost} onClick={() => setStatus(p, "void")}>Void</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!payments.length && (
                      <tr><td style={s.td} colSpan={8}><span style={s.small}>No payments yet.</span></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={s.small}>Void keeps the line for your records but leaves it out of every total. Nothing is ever deleted from here.</div>
            </div>

            {Object.keys(monthsByCurrency).length > 0 && (
              <div style={s.panel}>
                <h2 style={s.h2}>Paid per month</h2>
                <div style={s.row}>
                  {Object.entries(monthsByCurrency).map(([cur, list]) => {
                    const max = Math.max(...list.map((m) => m.total), 1);
                    return (
                      <div key={cur} style={{ flex: "1 1 280px", display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={s.small}>{cur}</div>
                        {list.slice(0, 12).map((m) => (
                          <div key={m.month} style={{ display: "grid", gridTemplateColumns: "64px 1fr auto", gap: 8, alignItems: "center", fontSize: "0.78rem" }}>
                            <span style={{ color: palette.mutedText }}>{m.month}</span>
                            <div style={{ height: 8, background: palette.field, borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ width: `${(m.total / max) * 100}%`, height: "100%", background: palette.up }} />
                            </div>
                            <span>{money(m.total, cur)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div style={{ flex: "1 1 200px", minWidth: 170, background: palette.glass, border: `1px solid ${palette.border}`, borderRadius: 6, padding: "12px 14px" }}>
      <div style={{ fontSize: "0.7rem", color: palette.mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontFamily: fonts.display, fontSize: "1.4rem", fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: "0.72rem", color: palette.mutedText, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
